import React, { useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import {
  IMAGE_FILE_SIZE_LIMIT,
  IMAGE_FILE_SIZE_LIMIT_VIOLATED_MESSAGE,
} from 'helpers/constants';
import { LoadingButton, Modal } from '@spotify-internal/anchor-ds';
import { ButtonSecondary } from '@spotify-internal/encore-web';
import { Range } from '@spotify-internal/encore-web/content-platform';
import { css } from 'emotion';
import { SM_SCREEN_MAX } from 'client/modules/Styleguide';
import styled from '@emotion/styled';
import events from './events';

/* Use important to ensure that image scales well on small screens */
const AvatarCanvas = styled(AvatarEditor)`
  @media (max-width: ${SM_SCREEN_MAX}px) {
    width: 250px !important;
    height: 250px !important;
  }
`;

export type ImageCropperProps = {
  screen: string;
  onCancel: () => void;
  imageFile: File;
  onImageCropError: (errorMessage: string) => void;
  onImageCrop: (croppedBlob: Blob) => Promise<void>;
};

const ImageCropper = (props: ImageCropperProps) => {
  const { screen, onCancel, imageFile, onImageCropError, onImageCrop } = props;

  const [isSaving, setIsSaving] = useState(false);
  const [scale, setScale] = useState(1.0);
  const avatarEditorElement = React.useRef<AvatarEditor>(null);

  const handleClickSaveImage = () => {
    setIsSaving(true);

    if (avatarEditorElement.current) {
      // This returns a HTMLCanvasElement, it can be made into a data URL or a blob,
      // drawn on another canvas, or added to the DOM.

      const canvas = avatarEditorElement.current.getImage();
      if (!canvas.toBlob) {
        setIsSaving(false);
        return;
      }
      canvas.toBlob((imageBlob: Blob | null) => {
        if (imageBlob) {
          if (imageBlob.size > IMAGE_FILE_SIZE_LIMIT) {
            onImageCropError(IMAGE_FILE_SIZE_LIMIT_VIOLATED_MESSAGE);
            setIsSaving(false);
          } else {
            onImageCrop(imageBlob).then(() => {
              events.newImageSaved({ location: screen });
            });
          }
        }
      });
    } else {
      setIsSaving(false);
    }
  };

  const handleChangeScale = (value: number) => {
    setScale(value);
  };

  return (
    <>
      <AvatarCanvas
        image={imageFile}
        width={400}
        height={400}
        color={[255, 255, 255, 0.2]} // RGBA
        ref={avatarEditorElement}
        scale={scale}
      />
      <div
        className={css`
          max-width: 200px;
          margin: auto;
        `}
      >
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label>
          <span
            className={css`
              display: block;
              margin-bottom: 8px;
            `}
          >
            Zoom
          </span>
          <Range
            aria-labelledby="zoom-range"
            onChange={handleChangeScale}
            min={1}
            step={0.1}
            max={4}
            value={scale}
          />
        </label>
      </div>
      <Modal.Footer>
        <ButtonSecondary onClick={onCancel}>Cancel</ButtonSecondary>
        <LoadingButton isLoading={isSaving} onClick={handleClickSaveImage}>
          Save
        </LoadingButton>
      </Modal.Footer>
    </>
  );
};

export { ImageCropper };
